import React, { useEffect, useState } from "react";
import { getAdminProfile, changeAdminPassword } from "../../API/adminDetails";
import { getCoachDetails, changeCoachPassword } from "../../API/coach-mgmt";
import { SUCCESS_TOAST } from "../../helpers/toasters";
import { useSelector } from "react-redux";
import { DefaultReduxState } from "../../store";

interface Batch {
    _id: string;
    name: string;
    code?: string;
}

interface UserProfile {
    name: string;
    email: string;
    isSuper?: boolean;
    active?: boolean;
    trainingModel?: string;
    dob?: string;
    contact?: number;
    address?: string;
    city?: string;
    country?: string;
    batches?: Batch[]; // Or any type that matches your batch references
}


const UserProfile: React.FC = () => {
    const user = useSelector((state: DefaultReduxState) => state.auth);
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [currentPassword, setCurrentPassword] = useState<string>("");
    const [newPassword, setNewPassword] = useState<string>("");
    const [confirmPassword, setConfirmPassword] = useState<string>("");

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const token = localStorage.getItem("refToken");
                if (!token) {
                    setError("No authentication token found.");
                    setLoading(false);
                    return;
                }

                const data = user.isCoach
                    ? await getCoachDetails(user._id)
                    : await getAdminProfile(token);

                setProfile(data);
            } catch (err) {
                setError("Failed to load profile.");
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [user]);

    const handlePasswordChange = async () => {
        try {
            const trimmedCurrentPassword = currentPassword.trim();
            const trimmedNewPassword = newPassword.trim();
            const trimmedConfirmPassword = confirmPassword.trim();

            if (!trimmedCurrentPassword || !trimmedNewPassword || !trimmedConfirmPassword) {
                setError("All password fields are required.");
                return;
            }

            if (trimmedNewPassword.length < 6) {
                setError("New password must be at least 6 characters long.");
                return;
            }

            if (trimmedNewPassword !== trimmedConfirmPassword) {
                setError("New password and confirm password must match.");
                return;
            }

            const token = localStorage.getItem("refToken");
            if (!token) {
                setError("No authentication token found.");
                return;
            }

            if (!profile) {
                setError("Profile data is missing.");
                return;
            }

            

            let result;

            if (user.isCoach) {
                result = await changeCoachPassword(
                    token,
                    trimmedCurrentPassword,
                    trimmedNewPassword,
                    trimmedConfirmPassword
                );
            } else {
                result = await changeAdminPassword(
                    token,
                    trimmedCurrentPassword,
                    trimmedNewPassword,
                    trimmedConfirmPassword
                );
            }

            

            if (result && result.message) {
                SUCCESS_TOAST(result.message);
                setIsModalOpen(false);
                setCurrentPassword("");
                setNewPassword("");
                setConfirmPassword("");
            } else {
                setError("Password change failed. No message returned.");
            }
        } catch (error: any) {
            console.error("Error changing password:", error);
            setError(error.response?.data?.message || "Failed to change password.");
        }
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div style={errorStyle}>{error}</div>;
    if (!profile) return <div>No admin details available.</div>;

    return (
        <div style={containerStyle}>
            <h2 style={headerStyle}>
                {user.isCoach
                    ? "Coach Profile"
                    : user.isSuper
                        ? "Super Admin Profile"
                        : "Admin Profile"}
            </h2>
            <p><strong>Name:</strong> {profile.name}</p>
            <p><strong>Email:</strong> {profile.email}</p>

            {user.isCoach ? (
                <>
                    <p><strong>Active:</strong> {profile.active ? "Yes" : "No"}</p>
                    <p><strong>Training Model:</strong> {profile.trainingModel || "N/A"}</p>
                    <p><strong>Date of Birth:</strong> {profile.dob ? new Date(profile.dob).toLocaleDateString() : "N/A"}</p>
                    <p><strong>Contact:</strong> {profile.contact || "N/A"}</p>
                    <p><strong>Address:</strong> {profile.address || "N/A"}</p>
                    <p><strong>City:</strong> {profile.city || "N/A"}</p>
                    <p><strong>Country:</strong> {profile.country || "N/A"}</p>
                    <p><strong>Batches:</strong> {profile.batches && profile.batches.length > 0
                        ? profile.batches.map(batch => batch.name).join(", ")
                        : "None"}
                    </p>
                </>
            ) : (
                <p><strong>Super Admin:</strong> {user.isSuper ? "Yes" : "No"}</p>
            )}

            <button onClick={() => setIsModalOpen(true)} style={buttonStyle}>Change Password</button>

            {isModalOpen && (
                <div style={modalOverlayStyle}>
                    <div style={modalStyle}>
                        <h3>Change Password</h3>
                        <div>
                            <label>Current Password:</label>
                            <input
                                type="password"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                style={inputStyle}
                            />
                        </div>
                        <div>
                            <label>New Password:</label>
                            <input
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                style={inputStyle}
                            />
                        </div>
                        <div>
                            <label>Confirm New Password:</label>
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                style={inputStyle}
                            />
                        </div>
                        <button onClick={handlePasswordChange} style={buttonStyle}>Change Password</button>
                        <button onClick={() => setIsModalOpen(false)} style={buttonStyle}>Close</button>
                    </div>
                </div>
            )}
        </div>
    );
};

// Define styles correctly as separate objects
const containerStyle: React.CSSProperties = {
    padding: "20px",
    maxWidth: "800px",
    margin: "0 auto",
    border: "1px solid #ddd",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
};

const headerStyle: React.CSSProperties = {
    textAlign: "center",
};

const errorStyle: React.CSSProperties = {
    color: "red",
    textAlign: "center",
};

const buttonStyle: React.CSSProperties = {
    padding: "10px 20px",
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    margin: "20px",
};

const inputStyle: React.CSSProperties = {
    padding: "8px",
    margin: "5px 0",
    width: "80%",
    borderRadius: "5px",
    border: "1px solid #ddd",
};

const modalOverlayStyle: React.CSSProperties = {
    position: "fixed",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
};

const modalStyle: React.CSSProperties = {
    backgroundColor: "white",
    padding: "20px",
    borderRadius: "8px",
    width: "600px",
    textAlign: "center",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
};

export default UserProfile;
